import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://bikertechie.com';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/assets/logo.png`;

/**
 * Applies per-route document metadata.
 *
 * The app is a client-rendered SPA, so every route otherwise inherits the single
 * <title>/<meta description> baked into public/index.html — which makes all pages
 * look identical to search engines. This writes the real values on mount.
 *
 * Tags created here are tagged with data-seo so a later route can overwrite them
 * instead of appending duplicates.
 */
const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export const useSEO = ({ title, description, path, image, noindex = false }) => {
  useEffect(() => {
    const url = path ? `${SITE_ORIGIN}${path}` : undefined;

    if (title) document.title = title;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image || DEFAULT_IMAGE);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'BikerTechie');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image || DEFAULT_IMAGE);

    setCanonical(url);
  }, [title, description, path, image, noindex]);
};

export default useSEO;
